import { createFileRoute } from "@tanstack/react-router";
import { ProductPage } from "@/components/site/ProductPage";
import { HapTimelinePanel } from "@/components/site/HapTimelinePanel";
import { hapTimelineHowToJsonLd } from "@/components/site/HapTimelinePanel.seo";
import { HqsChecklistDownload } from "@/components/site/HqsChecklistDownload";
import { Section8Calculators } from "@/components/site/Section8Calculators";
import { Section8Resources } from "@/components/site/Section8Resources";
import { Section8CaseStudy } from "@/components/site/Section8CaseStudy";
import { Section8StateExplorer } from "@/components/site/Section8StateExplorer";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { buildHead, mergeHead } from "@/lib/seo-head";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";

const SLUG = "section-8-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG);
const URL = "/section-8-loans";

const KEYWORDS = [
  "Section 8 loan",
  "Section 8 DSCR loan",
  "HAP contract loan",
  "Section 8 rental property financing",
  "Section 8 landlord loan",
  "housing choice voucher rental loan",
  "Section 8 refinance",
  "Section 8 investment property mortgage",
  "guaranteed rent DSCR loan",
  "HQS inspection rental loan",
  "Section 8 LLC loan",
  "Section 8 cash out refinance",
].join(", ");

export const Route = createFileRoute("/section-8-loans/")({
  head: () =>
    mergeHead(
      buildHead({
        title: "Section 8 DSCR Loans for Rental Property",
        description:
          "Buy or refinance Section 8 rental property using your Housing Assistance Payment contract for guaranteed rent qualification.",
        url: URL,
        keywords: KEYWORDS,
        image: HERO?.src,
        imageAlt: HERO?.alt,
        breadcrumbs: [
          { name: "Home", url: "/" },
          { name: PRODUCT.shortName, url: URL },
        ],
        service: { name: PRODUCT.name },
        faqs: PRODUCT.faq.map((f) => ({ q: f.q, a: f.a })),
      }),
      {
        scripts: [
          { type: "application/ld+json", children: JSON.stringify(hapTimelineHowToJsonLd(URL)) },
        ],
      },
    ),
  component: () => (
    <ProductPage
      product={PRODUCT}
      hideBaseTools
      topTools={
        <div className="space-y-8">
          <Section8Calculators locationLabel="your target market" />
          <LiveDscrRateSection
            strategyCards
            id="live-rates"
            eyebrow={"Live pricing · DSCR"}
            heading={"Live DSCR pricing for a Section 8 rental scenario"}
            paragraphs={[
              "These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property - rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.",
            ]}
            links={[
              { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
              { to: "/rates", label: "See today's investor rate bands" },
            ]}
          />
        </div>
      }
      extraTools={
        <div className="space-y-8">
          <Section8StateExplorer />
          <HapTimelinePanel />
          <Section8CaseStudy location="a Section 8 investor market" />
          <HqsChecklistDownload />
          <Section8Resources />
        </div>
      }
    />
  ),
});

